import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { StatsRecapResponse } from "@/types/api/containers-recap";
import { ChevronRight } from "@untitledui/icons";

function pct(part: number, whole: number): string {
    if (whole <= 0) return "—";
    return `${Math.round((part / whole) * 100)}%`;
}

function sumRiskLabels(dist: { label: string; value: number }[], labels: string[]): number {
    const set = new Set(labels.map((l) => l.toLowerCase()));
    return dist.filter((d) => set.has(d.label.toLowerCase())).reduce((s, d) => s + d.value, 0);
}

export type ShipperControlHubPerformanceClustersProps = {
    stats: StatsRecapResponse | undefined;
    buildContainersUrl: (params: Record<string, string>) => string;
    issuesHref: string;
    reviewHref: string;
    pendingReviewCount: number;
};

/**
 * Tre riquadri sintetici: documenti, rischio, attrito operativo — linguaggio operatore e CTA chiare.
 */
export function ShipperControlHubPerformanceClusters({
    stats,
    buildContainersUrl,
    issuesHref,
    reviewHref,
    pendingReviewCount,
}: ShipperControlHubPerformanceClustersProps) {
    const { t } = useTranslation(["pages:dashboard", "common"]);

    const total = stats?.total_containers ?? 0;
    const withCompleteDocs = stats?.containers_with_complete_docs ?? 0;
    const docsPrimary = total > 0 ? pct(withCompleteDocs, total) : "—";

    const pendingDocs = stats?.pending_documents ?? 0;
    const docIssues = stats?.doc_issue_count ?? stats?.containers_with_doc_issues ?? 0;
    const crossCheck = stats?.cross_check_issue_count ?? 0;
    const missing = stats?.missing_document_count ?? 0;

    const dist = stats?.risk_distribution ?? [];
    const highCrit = sumRiskLabels(dist, ["high", "critical"]);
    const medHighCrit = sumRiskLabels(dist, ["medium", "high", "critical"]);
    const riskPrimary = total > 0 ? pct(medHighCrit, total) : "—";

    const nonDocIssues = stats?.non_doc_issue_count ?? 0;
    const inTransit = stats?.in_transit ?? 0;
    const aboutToDepart = stats?.about_to_depart_count ?? 0;

    return (
        <section aria-labelledby="control-hub-clusters-heading">
            <h2 id="control-hub-clusters-heading" className="sr-only">
                {t("controlHub.clusters.sectionTitle")}
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-stretch">
                {/* Documenti */}
                <div className="flex h-full min-h-0 flex-col rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
                    <p className="text-xs font-medium uppercase tracking-wide text-brand-secondary">
                        {t("controlHub.clusters.docs.eyebrow")}
                    </p>
                    <h3 className="mt-1 text-base font-semibold text-primary">{t("controlHub.clusters.docs.title")}</h3>
                    <p className="mt-1 text-sm text-tertiary">{t("controlHub.clusters.docs.subtitle")}</p>
                    <p className="mt-4 text-3xl font-semibold tabular-nums text-primary">{docsPrimary}</p>
                    <p className="mt-1 text-xs text-quaternary">{t("controlHub.clusters.docs.primaryHint")}</p>
                    <ul className="mt-4 space-y-2 text-sm text-secondary">
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.docs.linePending")}</span>
                            <span className="font-medium tabular-nums text-primary">{pendingDocs}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.docs.lineDocIssues")}</span>
                            <span className="font-medium tabular-nums text-primary">{docIssues}</span>
                        </li>
                        {(missing > 0 || crossCheck > 0) && (
                            <li className="flex justify-between gap-2">
                                <span>{t("controlHub.clusters.docs.lineCrossMissing")}</span>
                                <span className="font-medium tabular-nums text-primary">
                                    {missing + crossCheck}
                                </span>
                            </li>
                        )}
                    </ul>
                    <div className="mt-auto pt-4">
                        <Link
                            to={buildContainersUrl({ has_doc_issues: "true" })}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            {t("controlHub.clusters.docs.cta")}
                            <ChevronRight className="size-4" />
                        </Link>
                    </div>
                </div>

                {/* Rischio */}
                <div className="flex h-full min-h-0 flex-col rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
                    <p className="text-xs font-medium uppercase tracking-wide text-brand-secondary">
                        {t("controlHub.clusters.risk.eyebrow")}
                    </p>
                    <h3 className="mt-1 text-base font-semibold text-primary">{t("controlHub.clusters.risk.title")}</h3>
                    <p className="mt-1 text-sm text-tertiary">{t("controlHub.clusters.risk.subtitle")}</p>
                    <p className="mt-4 text-3xl font-semibold tabular-nums text-primary">{riskPrimary}</p>
                    <p className="mt-1 text-xs text-quaternary">{t("controlHub.clusters.risk.primaryHint")}</p>
                    <ul className="mt-4 space-y-2 text-sm text-secondary">
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.risk.lineHighCritical")}</span>
                            <span className="font-medium tabular-nums text-primary">{highCrit}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.risk.lineMediumPlus")}</span>
                            <span className="font-medium tabular-nums text-primary">{medHighCrit}</span>
                        </li>
                    </ul>
                    <div className="mt-auto pt-4">
                        <Link
                            to={buildContainersUrl({ risk: "medium,high,critical" })}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            {t("controlHub.clusters.risk.cta")}
                            <ChevronRight className="size-4" />
                        </Link>
                    </div>
                </div>

                {/* Attrito */}
                <div className="flex h-full min-h-0 flex-col rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
                    <p className="text-xs font-medium uppercase tracking-wide text-brand-secondary">
                        {t("controlHub.clusters.friction.eyebrow")}
                    </p>
                    <h3 className="mt-1 text-base font-semibold text-primary">{t("controlHub.clusters.friction.title")}</h3>
                    <p className="mt-1 text-sm text-tertiary">{t("controlHub.clusters.friction.subtitle")}</p>
                    <p className="mt-4 text-3xl font-semibold tabular-nums text-primary">
                        {nonDocIssues > 0 ? nonDocIssues : "—"}
                    </p>
                    <p className="mt-1 text-xs text-quaternary">{t("controlHub.clusters.friction.primaryHint")}</p>
                    <ul className="mt-4 space-y-2 text-sm text-secondary">
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.friction.lineInTransit")}</span>
                            <span className="font-medium tabular-nums text-primary">{inTransit}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.friction.lineAboutToDepart")}</span>
                            <span className="font-medium tabular-nums text-primary">{aboutToDepart}</span>
                        </li>
                        <li className="flex justify-between gap-2">
                            <span>{t("controlHub.clusters.friction.lineReviewCenter")}</span>
                            <span className="font-medium tabular-nums text-primary">{pendingReviewCount}</span>
                        </li>
                    </ul>
                    <div className="mt-auto flex flex-wrap gap-3 pt-4">
                        <Link
                            to={issuesHref}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            {t("controlHub.clusters.friction.ctaIssues")}
                            <ChevronRight className="size-4" />
                        </Link>
                        <Link
                            to={reviewHref}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            {t("controlHub.clusters.friction.ctaReview")}
                            <ChevronRight className="size-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
